import { Form, Formik } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Confirm from '../../../components/confirm';
import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canWriteUser, getAdminUserDetail, getErrorObject,
  getUserDetail, goToRoutePage, isNullOrWhiteSpace, redirectRoutePage
} from '../../../utils/helper';
import { DeleteIcon, LockIcon, LockOpenIcon, NewIcon } from '../../../utils/icons';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, FormGroup, FormLabel, Grid, IconButton, List, ListItem,
  ListItemSecondaryAction, ListItemText, Switch, TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';

interface userAdminCRUDProps {
  open: boolean;
  userKey: string;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

// Default form value
const formDefaultValue = {
  firstName: "",
  lastLoginDate: "",
  lastName: "",
  roles: [] as AdminUserRole[],
  userId: "",
  userKey: "",
  isInternal: false,
};

const formDefaultTouchValue = {
  firstName: false,
  lastName: false,
  userId: false,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required("First name is required."),
  lastName: Yup.string().trim().required("Last name is required."),
  userId: Yup.string().trim().required("Email is required.").email("Email is required."),
});

const UserAdminCRUD = ({ open, userKey, onCallback }: userAdminCRUDProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const formReference = useRef(null) as any;

  const { showAlert, userFunctionAccess, referenceLookups, adminUsers } = useContext(AppContext);

  const [userExists, setUserExists] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<AdminUser>(formDefaultValue);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [categoryNames, setCategoryNames] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleName, setRoleName] = useState<string>("");

  const saveUser = async (mode: string) => {
    if (!canWriteUser(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("home");
      return;
    }

    const permissions: string[] = [];

    if (mode === "save") {
      formReference.current.values.roles.forEach((item) => {
        permissions.push(item.roleKey);
      });
    }

    const payload = {
      adminUser: {
        userId: formReference.current.values.userId,
        firstName: formReference.current.values.firstName,
        lastName: formReference.current.values.lastName,
        userKey: formReference.current.values.userKey,
        isInternal: formReference.current.values.isInternal,
        roleKeys: permissions,
      },
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/admin/users/" + formReference.current.values.userKey, payload)
        .then(async (response) => {
          showAlert({
            message: mode === "save" ? "Successfully saved." : "Successfully deleted.",
            severity: "success",
          });
          if (response.data.resetSite) {
            redirectRoutePage("");
            return;
          }
          onCallback({ status: "refresh", data: "" });
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "program.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save program data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const deleteUser = (confirm: boolean) => {
    setDeleteConfirmDialogOpen(false);
    if (confirm) {
      saveUser("delete");
    }
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmDialogOpen(true);
  };

  const cancelUser = () => {
    formReference.current.resetForm({});
    onCallback({ status: "cancel", data: "" });
  };

  const cancel = () => {
    setRoles([]);
    onCallback({ status: "cancel", data: "" });
  };

  const getUserDetailByEmail = async () => {


    setUserExists(false);

    if (formReference.current.errors.hasOwnProperty("userId")) {
      return;
    }

    if (isNew) {
      if (adminUsers.filter(adminUser => adminUser.userId.toLowerCase().trim() === formReference.current.values.userId.toLowerCase().trim()).length > 0) {
        formReference.current.setFieldError("userId", "The email has already been set up for application access.");
        return;
      }
    }

    setSaveInProgress(true);

    const apiResponse = await getUserDetail(formReference.current.values.userId);

    try {
      if (apiResponse.success) {
        const data = apiResponse.data;
        formReference.current.setFieldValue("firstName", data.user.information.first);
        formReference.current.setFieldValue("lastName", data.user.information.last);
        setRoleName(data.roleName);
        setUserExists(true);
      }
      else {
        formReference.current.setFieldError("userId", "Email not found in meshify.");
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
      }
    }
    catch (e) {
      showAlert({ message: "Something went wrong in our system.", severity: "error" });
    }

    setSaveInProgress(false);
  }

  const getUserDetailAsync = async () => {
    const apiResponse = await getAdminUserDetail(userKey);
    if (apiResponse.success) {
      const data = apiResponse.data;
      setInitialFormValues(data.adminUser as AdminUser);
      getRoles(data.adminUser.isInternal);
      setTimeout(async () => {
        setLoading2(false);
        await getUserDetailByEmail();
      }, 1000);

    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      cancelUser();
      return;
    }
  };

  const accessExists = (assignedRoles: AdminUserRole[], roleKey: string) => {
    const roleExists = assignedRoles.find((role) => {
      return role.roleKey.toUpperCase() === roleKey.toUpperCase();
    })?.roleKey;
    if (roleExists !== undefined) {
      return true;
    }
    return false;
  };

  const isNewAccess = (assignedRoles: AdminUserRole[], roleKey: string) => {
    const userRoleKey = assignedRoles.find((role) => {
      return role.roleKey.toUpperCase() === roleKey.toUpperCase();
    })?.userRoleKey;
    if (userRoleKey === "") {
      return true;
    }

    return false;
  };

  const addAccess = (role: Role) => {
    if (!accessExists(formReference.current.values.roles, role.roleKey)) {
      const roles = formReference.current.values.roles;
      roles.push({
        roleKey: role.roleKey,
        roleName: role.name,
        userRoleKey: "",
      });
      formReference.current.setFieldValue("roles", roles);
      formReference.current.setFieldTouched("roles", true);
    }
  };

  const deleteAccess = (role: Role) => {
    if (accessExists(formReference.current.values.roles, role.roleKey)) {
      const roles = formReference.current.values.roles.filter((item) => item.roleKey.toUpperCase() !== role.roleKey.toUpperCase());
      formReference.current.setFieldValue("roles", roles);
      formReference.current.setFieldTouched("roles", true);
    }
  };

  // Is internal input changed
  const handleIsInternalChanged = (internal: boolean) => {
    formReference.current.setFieldValue("roles", []);
    formReference.current.setFieldTouched("roles", true);
    initialFormValues.roles = [];
    setRoles([]);
    getRoles(internal);
  };

  // Get roles
  const getRoles = (internal: boolean) => {
    let filterRoles: Role[] = [];

    if (internal) {
      filterRoles = referenceLookups.roles.filter((x) => ((x.accessibility === 0 || x.accessibility === 1) && x.categoryName.toLowerCase() !== "api"));
    } else {
      filterRoles = referenceLookups.roles.filter((x) => ((x.accessibility === 0 || x.accessibility === 2) && x.categoryName.toLowerCase() !== "api"));
    }

    setRoles(filterRoles);

    const uniqueCategoryNames = filterRoles.map((item) => item.categoryName).filter((value, index, self) => self.indexOf(value) === index);

    setCategoryNames(uniqueCategoryNames);
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      if (open) {
        if (!canWriteUser(userFunctionAccess)) {
          showAlert({ message: "Access Denied", severity: "error" });
          goToRoutePage("home");
          return;
        }

        if (isNullOrWhiteSpace(userKey)) {
          showAlert({ message: "User key missing.", severity: "error" });
          goToRoutePage("home");
          return;
        }

        setLoading2(true);
        setUserExists(false);
        setSaveInProgress(false);

        setReadonly(!canWriteUser(userFunctionAccess));

        if (userKey === referenceLookups.newRecordDefaultId) {
          setInitialFormValues({
            ...formDefaultValue,
            userKey: referenceLookups.newRecordDefaultId,
          });
          setIsNew(true);
          setTimeout(() => {
            getRoles(formDefaultValue.isInternal);
            setLoading2(false);
          }, 3000);
        } else {
          await getUserDetailAsync();
          setIsNew(false);
        }
      }
    };
    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userKey]);

  return (
    <>
      {formReference.current !== null ? <Confirm title="Confirmation" content={`<p>Are you sure you want to delete this user?</p><p style="text-align:center"><b>${formReference.current.values.firstName} ${formReference.current.values.lastName}</b></p><p style="text-align:center"><b>${formReference.current.values.userId} </b></p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpen} callbackOnClose={() => setDeleteConfirmDialogOpen} callbackOnConfirm={deleteUser} /> : null}

      <Dialog className={globalStyles.dialogContainer} maxWidth={userExists ? "lg" : "sm"} fullWidth={true} open={open} aria-labelledby="input-dialog-title" aria-describedby="input-dialog-description">
        <DialogTitle id="input-dialog-title" className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            {readonly ? "View User & Premissions" : isNew ? "New User & Premissions" : "Manager User & Premissions"}
          </Typography>
        </DialogTitle>
        {loading2 ? (
          <>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent dividers>
              <SaveBackdrop open={saveInProgress} />

              <Box className={pageStyles.contentText}>
                <Formik
                  innerRef={formReference}
                  initialValues={initialFormValues}
                  validationSchema={formValidationSchema}
                  validateOnMount={true}
                  initialTouched={formDefaultTouchValue}
                  validateOnBlur={true}
                  validateOnChange={true}
                  onSubmit={(values: AdminUser) => {
                    saveUser("save");
                  }}
                >
                  {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, resetForm }) => (
                    <Form>
                      <Grid container spacing={3}>
                        {
                          isNew && !userExists ?
                            <Grid item xs={12}>
                              <Grid container spacing={3}>
                                <Grid item xs={12}>
                                  <TextField required disabled={readonly || !isNew} id="userId" name="userId" label="Email" value={values.userId} onChange={handleChange} onBlur={handleBlur} error={touched.userId && Boolean(errors.userId)} helperText={errors.userId && touched.userId ? errors.userId : null} />
                                </Grid>
                              </Grid>
                            </Grid>
                            : <>
                              <Grid item xs={12} md={4}>
                                <TextField disabled={true} label="Username" value={`${values.firstName} ${values.lastName}`} />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField disabled={true} label="Meshify Role" value={roleName} onChange={handleChange} />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField disabled={true} id="userId" name="userId" label="Email" value={values.userId} onBlur={handleBlur} error={touched.userId && Boolean(errors.userId)} helperText={errors.userId && touched.userId ? errors.userId : null} />
                              </Grid>
                              <Grid item xs={12} md={4} >

                                <FormControl className={globalStyles.formControl} >
                                  <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                    User Type
                                  </FormLabel>
                                  <Box display="flex" flexDirection="row" justifyContent="left" alignItems="center" >
                                    <Box>External&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Box>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            disabled={readonly}
                                            id="isInternal"
                                            name="isInternal"
                                            checked={values.isInternal}
                                            onChange={(ev, checked) => {
                                              setFieldValue("isInternal", checked);
                                              handleIsInternalChanged(checked);
                                            }}
                                          />
                                        }
                                        label={""}
                                        labelPlacement="end"
                                      />
                                    </FormGroup>
                                    <Box>Internal</Box>

                                  </Box>

                                </FormControl>

                              </Grid>
                              {/* <Grid item xs={12} md={8} >
                                <Typography variant='subtitle1'>
                                  <small>
                                  Assign permissions by differentiating between internal and external users, and apply distinct access controls to each group accordingly.
                                  </small>
                                </Typography>
                              </Grid> */}
                              <Grid item xs={12}>
                                <Alert severity='info'>
                                  Assign permissions that match a user's role or responsibilities. Before saving, carefully review the assigned permissions to ensure accuracy and prevent potential security risks.
                                </Alert>
                              </Grid>
                              <Grid item xs={12}>
                                <Grid container spacing={3}>
                                  {categoryNames.map((categoryName, index) => (
                                    <Grid item xs={12} md={6} key={index}>
                                      <Box className={globalStyles.topic}>{categoryName}</Box>
                                      <Box p={2} style={{ fontSize: "0.8em" }}>
                                        {roles.filter((x) => x.categoryName === categoryName).length > 0 ? <>{roles.filter((x) => x.categoryName === categoryName)[0].categoryNote}</> : null}
                                      </Box>
                                      <List>
                                        {roles
                                          .filter((role) => role.categoryName === categoryName)
                                          .map((role, index) =>
                                            accessExists(values.roles, role.roleKey) ? (
                                              <ListItem key={index}>
                                                <ListItemText primary={role.name} />
                                                <ListItemSecondaryAction>
                                                  {!readonly ? (
                                                    <>
                                                      {isNewAccess(values.roles, role.roleKey) ? (
                                                        <IconButton edge="end">
                                                          <Tooltip arrow title="New access">
                                                            <NewIcon fontSize="medium" color="action" onClick={() => deleteAccess(role)} />
                                                          </Tooltip>
                                                        </IconButton>
                                                      ) : accessExists(values.roles, role.roleKey) ? (
                                                        <IconButton edge="end">
                                                          <Tooltip arrow title="Remove access">
                                                            <LockOpenIcon fontSize="medium" color="secondary" onClick={() => deleteAccess(role)} />
                                                          </Tooltip>
                                                        </IconButton>
                                                      ) : null}
                                                    </>
                                                  ) : null}
                                                  <></>
                                                </ListItemSecondaryAction>
                                              </ListItem>
                                            ) : (
                                              <ListItem key={index}>
                                                <ListItemText primary={role.name} />
                                                <ListItemSecondaryAction>
                                                  {!readonly ? (
                                                    <>
                                                      {accessExists(initialFormValues.roles, role.roleKey) ? (
                                                        <IconButton edge="end">
                                                          <Tooltip arrow title="Add access">
                                                            <DeleteIcon fontSize="medium" color="error" onClick={() => addAccess(role)} />
                                                          </Tooltip>
                                                        </IconButton>
                                                      ) : !accessExists(values.roles, role.roleKey) ? (
                                                        <IconButton edge="end">
                                                          <Tooltip arrow title="Add access">
                                                            <LockIcon fontSize="medium" color="error" onClick={() => addAccess(role)} />
                                                          </Tooltip>
                                                        </IconButton>
                                                      ) : null}
                                                    </>
                                                  ) : null}
                                                  <></>
                                                </ListItemSecondaryAction>
                                              </ListItem>
                                            )
                                          )}
                                      </List>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Grid>
                            </>
                        }

                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Box>
            </DialogContent>
            <DialogActions>
              {userKey !== "" && !readonly && !isNew && userExists ? (
                <Tooltip arrow title="Delete this user account">
                  <Button onClick={() => openDeleteConfirm()} className={globalStyles.errorText}>
                    <DeleteIcon color="error" />
                  </Button>
                </Tooltip>
              ) : null}

              {!readonly && isNew && !userExists ? (
                <Button onClick={() => getUserDetailByEmail()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  Check In Meshify
                </Button>
              ) : null}

              {!readonly && userExists ? (
                <Button onClick={() => formReference.current.handleSubmit()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  {isNew ? "Save" : "Update"}
                </Button>
              ) : null}

              <Button onClick={() => cancelUser()} autoFocus className={globalStyles.buttonDefault}>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContainer: {
      // height: "calc(100vh - 500px)",
      // overflow: "auto"
    },
    contentText: {
      whiteSpace: "pre-wrap",
      minHeight: "150px",
    },
  })
);

export default UserAdminCRUD;
